import { SupabaseClient } from "@supabase/supabase-js";

/**
 * Extract YouTube video ID from various URL formats
 */
export const extractYouTubeId = (url: string): string | null => {
  if (!url) return null;
  
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\?\/]+)/,
    /^([a-zA-Z0-9_-]{11})$/,
  ];
  
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  
  return null;
};

/**
 * Get YouTube thumbnail URL from video URL
 */
export const getYouTubeThumbnail = (url: string): string => {
  const videoId = extractYouTubeId(url);
  return videoId ? `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg` : "";
};

/**
 * Check if a YouTube video already exists in another blog post
 */
export const checkDuplicateVideo = async (
  supabase: SupabaseClient,
  videoUrl: string,
  currentPostId?: string
): Promise<{ isDuplicate: boolean; existingPost?: { id: string; title: string; slug: string } }> => {
  const videoId = extractYouTubeId(videoUrl);
  
  if (!videoId) {
    return { isDuplicate: false };
  }

  // Query all posts with video URLs
  const { data: posts, error } = await supabase
    .from("blog_posts")
    .select("id, title, slug, video_url")
    .not("video_url", "is", null);

  if (error || !posts) {
    console.error("Error checking duplicate videos:", error);
    return { isDuplicate: false };
  }

  // Check for matching video ID (excluding current post if editing)
  for (const post of posts) {
    if (currentPostId && post.id === currentPostId) continue;
    
    const existingVideoId = extractYouTubeId(post.video_url);
    
    if (existingVideoId === videoId) {
      return {
        isDuplicate: true,
        existingPost: {
          id: post.id,
          title: post.title,
          slug: post.slug,
        },
      };
    }
  }

  return { isDuplicate: false };
};
